import React, {useEffect, useState, useRef} from "react";
import {toast} from "@/components/ui/use-toast";
import Navbar from "@/components/Navbar/Navbar";
import SideMenu from "@/components/MyAccount/SideMenu";
import ApiHttpClient from "@/HttpClient";
import DatePicker from "react-datepicker";
import {DeliveryNoteListResponse, DeliveryNoteModel} from "@/api/Models";

const handleDownloadDeliveryNotePDF = async (deliveryNoteNumber: string) => {
    try {
        const response = await ApiHttpClient().get(`/deliveryNote/pdf/${deliveryNoteNumber}`);

        if (response.status === 200) {
            const {pdfBytes, fileName} = response.data;
            // Decode the base64 string
            const byteCharacters = atob(pdfBytes);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);

            // Create a Blob from the byteArray
            const blob = new Blob([byteArray], {type: 'application/pdf'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', fileName || 'Aviz.pdf');
            document.body.appendChild(link);
            link.click();

            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        } else {
            console.error('A aparut o eroare la descarcarea avizului, mesajul:', response.status);
        }
    } catch (error) {
        console.error('A aparut o eroare la descarcarea avizului', error);
    }
}

const handleDownloadDeliveryNoteConformityPDF = async (deliveryNoteNumber: string) => {
    try {
        const response = await ApiHttpClient().get(`/deliveryNote/pdf/conformity/${deliveryNoteNumber}`);

        if (response.status === 200) {
            const {pdfBytes, fileName} = response.data;
            // Decode the base64 string
            const byteCharacters = atob(pdfBytes);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);

            // Create a Blob from the byteArray
            const blob = new Blob([byteArray], {type: 'application/pdf'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', fileName || 'Declaratie_de_conformitate.pdf');
            document.body.appendChild(link);
            link.click();

            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        } else {
            console.error('A aparut o eroare la descarcarea declaratiei de conformitate, mesajul:', response.status);
        }
    } catch (error) {
        console.error('A aparut o eroare la descarcarea declaratiei de conformitate', error);
    }
}

const SearchInput: React.FC<any> = ({searchDeliveryNote, setSearchDeliveryNote, setIsNewSearch}) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length > 1) {
            setSearchDeliveryNote(e.target.value);
            setIsNewSearch(true);
        } else {
            setSearchDeliveryNote(e.target.value);
        }
    };

    return (
        <input
            className="block w-full rounded-lg border border-gray-200 p-3 text-gray-600 hover:border-black"
            type="search"
            value={searchDeliveryNote}
            onChange={handleChange}
            placeholder="Cauta dupa codul unui aviz de expeditie..."
        />
    );
};

export default function DeliveryNoteListPage() {
    const [currentPage, setCurrentPage] = useState(0);
    const [searchDeliveryNote, setSearchDeliveryNote] = useState("");
    const [isNewSearch, setIsNewSearch] = useState(false);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    const [data, setData] = useState<DeliveryNoteListResponse>();
    const [dateMin, setDateMin] = useState<any>();
    const [dateMax, setDateMax] = useState<any>(new Date());
    const pageSize = 30;

    const detailsRef = useRef<HTMLDetailsElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                detailsRef.current &&
                !detailsRef.current.contains(event.target as Node)
            ) {
                detailsRef.current.removeAttribute("open");
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handlePageChange = (selectedPage: any) => {
        setCurrentPage(selectedPage);
        setIsNewSearch(false);
    };

    const handleRemoveFilters = () => {
        setSearchDeliveryNote('');
        setIsNewSearch(false);
        setDateMin('');
        setDateMax(new Date());
    }

    useEffect(() => {
        const updateDeliveryNote = async () => {
            setIsLoadingSearch(true);

            let response;
            const filters = [];

            if (dateMin) {
                filters.push({
                    field: "deliveryNoteDate",
                    operator: "gte",
                    value: new Date(dateMin).toISOString()
                });
            }

            if(dateMax) {
                filters.push({
                    field: "deliveryNoteDate",
                    operator: "lte",
                    value: new Date(dateMax).toISOString()
                });
            }

            const requestBody = {
                advancedFilter: {
                    logic: "and",
                    filters: filters
                },
                pageNumber: currentPage + 1,
                pageSize: pageSize
            }

            const requestBodySearchAndDate = {
                advancedFilter: {
                    logic: "and",
                    filters: filters
                },
                advancedSearch: {
                    fields: [
                        "deliveryNoteNumber",
                        "shipping.orderId",
                        "shipping.order.preOrderId",
                        "workingPoint.workingPointName",
                    ],
                    keyword: searchDeliveryNote
                },
                pageNumber: currentPage + 1,
                pageSize: pageSize,
            }

            if (searchDeliveryNote) {
                if (isNewSearch) setCurrentPage(0);

                if (dateMin && dateMax) {
                    response = await  ApiHttpClient().post<DeliveryNoteListResponse>('/deliveryNote/search', requestBodySearchAndDate);
                } else {
                    response = await ApiHttpClient().post<DeliveryNoteListResponse>('/deliveryNote/search', {
                        "advancedSearch": {
                            "fields": [
                                "deliveryNoteNumber",
                                "orderId",
                                "order.preOrderId",
                                "workingPoint.workingPointName",
                            ],
                            "keyword": searchDeliveryNote
                        },
                        "pageNumber": currentPage + 1,
                        "pageSize": pageSize,
                    });
                }
            } else if (dateMin && dateMax) {
                response = await  ApiHttpClient().post<DeliveryNoteListResponse>(`/deliveryNote/search`, requestBody);
            } else {
                response = await ApiHttpClient().post<DeliveryNoteListResponse>(`/deliveryNote/search`, {
                    "keyword": "",
                    "pageNumber": currentPage + 1,
                    "pageSize": pageSize,
                    "orderBy": ["deliveryNoteDate Desc"]
                });
            }
            if (!response?.data) {
                toast({description: "A aparut o eroare pentru afisarea precomenzilor"});
                return <></>;
            }
            setIsLoadingSearch(false);
            setData(response.data);
        }

        updateDeliveryNote();
    }, [searchDeliveryNote, currentPage, pageSize, isNewSearch, dateMin, dateMax])

    return (
        <>
            <Navbar/>
            <div className="flex flex-row items-start">
                <SideMenu/>
                {/* Desktop Version */}
                <div className="hidden mx-auto md:block z-auto">
                    {!isLoadingSearch && data?.totalPages === 0 && <p className="mt-5 ml-5">Nu au fost gasite rezultate.</p>}
                    <div className=" relative overflow-x-auto ml-8 shadow-md sm:rounded-lg overflow-y-auto pt-4 pb-1">

                        <div className="flex justify-end mb-2 mr-2">
                            <button
                                className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700"
                                onClick={handleRemoveFilters}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    className="size-3"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                                </svg>

                                <p className="text-sm whitespace-nowrap ml-2">Elimina filtrele</p>
                            </button>
                        </div>

                        <div className="mb-5">
                            <SearchInput searchDeliveryNote={searchDeliveryNote} setSearchDeliveryNote={setSearchDeliveryNote}
                                         setIsNewSearch={setIsNewSearch}/>
                        </div>

                        <details ref={detailsRef} className="group relative mb-5">
                            <summary
                                className="mx-2 flex items-center gap-2 pb-1 text-gray-700 transition-colors [&::-webkit-details-marker]:hidden"
                            >
                                <span className="text-sm font-medium"> Cauta avizele din </span>

                                <span className="transition-transform group-open:-rotate-180">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.5"
                                        stroke="currentColor"
                                        className="size-4"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round"
                                            d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                                    </svg>
                                  </span>
                            </summary>

                            <div
                                className="z-100 w-64 divide-y divide-gray-300 rounded border border-gray-300 bg-white shadow-sm group-open:absolute group-open:start-0 group-open:top-8"
                            >
                                <div className="flex items-center justify-between px-3 py-2">
                                    <span className="text-sm text-gray-700"> Selecteaza datele </span>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setDateMin('');
                                            setDateMax(new Date())
                                        }}
                                        className="text-sm text-gray-700 underline transition-colors hover:text-gray-900"
                                    >
                                        Reset
                                    </button>
                                </div>

                                <div className="flex items-center gap-3 p-3">
                                    <label htmlFor="MinPrice">
                                        <span className="text-sm text-gray-700"> Data de inceput </span>

                                        <DatePicker
                                            selected={dateMin}
                                            onChange={(dateMin) => setDateMin(dateMin)}
                                            dateFormat="dd/MM/yyyy"
                                            className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                            popperPlacement="bottom-start"
                                            popperModifiers={[
                                                {
                                                    name: "preventOverflow",
                                                    options: {boundary: "viewport", tether: false},
                                                    fn: () => ({}),
                                                },
                                                {
                                                    name: "flip",
                                                    options: {fallbackPlacements: []}, // Prevents flipping above
                                                    fn: () => ({}),
                                                },
                                            ]}
                                            portalId="root-portal" // Ensures calendar is rendered outside restrictive containers
                                        />
                                    </label>

                                    <label htmlFor="MaxPrice">
                                        <span className="text-sm text-gray-700"> Data de sfarsit </span>

                                        <DatePicker
                                            selected={dateMax}
                                            onChange={(dateMax) => setDateMax(dateMax)}
                                            dateFormat="dd/MM/yyyy"
                                            className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                            popperPlacement="bottom-start"
                                            popperModifiers={[
                                                {
                                                    name: "preventOverflow",
                                                    options: {boundary: "viewport", tether: false},
                                                    fn: () => ({}),
                                                },
                                                {
                                                    name: "flip",
                                                    options: {fallbackPlacements: []},
                                                    fn: () => ({}),
                                                },
                                            ]}
                                            portalId="root-portal"
                                        />
                                    </label>
                                </div>
                            </div>
                        </details>

                        <table className="w-full text-sm text-left h-56 rtl:text-right text-gray-500 dark:text-gray-400">
                            <thead
                                className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                            <tr>
                                <th scope="col" className="px-6 py-3">
                                    Data generarii avizului
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Ultima actualizare
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Status aviz
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Nume magazin
                                </th>
                                <th scope="col" className="px-6 py-3 max-w-xl">
                                    Nume francizat
                                </th>
                                <th scope="col" className="px-6 py-3 max-w-xl">
                                    Nr aviz de expeditie
                                </th>
                                <th scope="col" className="px-6 py-3 max-w-xl">
                                    Nr precomanda
                                </th>
                                <th scope="col" className="px-6 py-3 max-w-xl">
                                    Nr comanda
                                </th>
                                <th scope="col" className="px-6 py-3 max-w-xl text-center">
                                    Editeaza
                                </th>
                                <th scope="col" className="px-6 py-3 max-w-xl text-center">
                                    Descarca
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {data?.items.map((deliveryNote: DeliveryNoteModel) => {
                                return (
                                    <tr className="odd:bg-white odd:dark:bg-gray-900 even:bg-gray-50 even:dark:bg-gray-800 border-b dark:border-gray-700">
                                        <td className="px-6 py-4">
                                            {deliveryNote.deliveryNoteDate?.split("T")[0]}
                                        </td>
                                        <td className="px-6 py-4">
                                            {deliveryNote.deliveryNoteDate?.split("T")[0]}
                                        </td>
                                        {deliveryNote.documentStatusId === 360 && <td className="px-6 py-4">
                                            Generat
                                        </td>}
                                        {deliveryNote.documentStatusId === 370 && <td className="px-6 py-4">
                                            In Progres
                                        </td>}
                                        {deliveryNote.documentStatusId === 380 && <td className="px-6 py-4">
                                            Finalizat
                                        </td>}
                                        {deliveryNote.documentStatusId === 390 && <td className="px-6 py-4">
                                            In Disputa
                                        </td>}
                                        {deliveryNote.documentStatusId === 400 && <td className="px-6 py-4">
                                            Disputa Solutionata
                                        </td>}
                                        {deliveryNote.documentStatusId === 410 && <td className="px-6 py-4">
                                            Anulat
                                        </td>}
                                        <td className="px-6 py-4">
                                            {deliveryNote.warehouseName}
                                        </td>
                                        <td className="px-6 py-4">
                                            {deliveryNote.workingPoint.workingPointName}
                                        </td>
                                        {deliveryNote.deliveryNoteNumber ? <td className="px-6 py-4">
                                            {deliveryNote.deliveryNoteSeries} {deliveryNote.deliveryNoteNumber}
                                        </td> : <td className="px-6 py-4"> - </td>}
                                        <td className="px-6 py-4">
                                            {deliveryNote.order.preOrderId}
                                        </td>
                                        <td className="px-6 py-4">
                                            {deliveryNote.orderId}
                                        </td>
                                        <td className="px-6 py-4">
                                            <a
                                                href={"/my-account/delivery-note/" + deliveryNote.id}
                                                className="text-blue-500 underline z-auto">Editeaza avizul</a>
                                        </td>
                                        <td className="px-6 py-4">
                                            {[380, 390, 400, 410].includes(deliveryNote.documentStatusId) ?
                                                <div className="flex flex-col text-center">
                                                    <button
                                                        onClick={() => handleDownloadDeliveryNotePDF(deliveryNote.deliveryNoteNumber)}
                                                        className="mb-1 font-medium text-red-600 hover:text-red-500">
                                                        Descarca Aviz
                                                    </button>
                                                    <button
                                                        onClick={() => handleDownloadDeliveryNoteConformityPDF(deliveryNote.deliveryNoteNumber)}
                                                        className="mb-1 font-medium text-red-600 hover:text-red-500">
                                                        Descarca Declaratia de conformitate
                                                    </button>
                                                </div> : <div className="flex flex-col text-center"> - </div>}
                                        </td>
                                    </tr>
                                );
                            })}

                            </tbody>
                        </table>
                    </div>

                    <div className="text-center mt-8 mb-2">
                        <div className="inline-flex text-center items-center justify-center gap-3">
                            <div
                                onClick={(e) => {
                                    if (currentPage >= 1) handlePageChange(currentPage - 1);
                                    e.preventDefault()
                                }}
                                className="inline-flex size-8 items-center justify-center rounded border border-gray-100 bg-white text-gray-900 rtl:rotate-180"
                            >
                                <span className="sr-only">Pagina anterioara</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-3 w-3"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>

                            <p className="text-xs text-gray-900">
                                {currentPage + 1}<span className="mx-0.25">/</span>
                                {data !== undefined && Math.ceil(data.totalCount / pageSize)}
                            </p>

                            {data !== undefined && <div
                                onClick={(e) => {
                                    if (currentPage < data.totalPages - 1) handlePageChange(currentPage + 1);
                                    e.preventDefault()
                                }}
                                className="inline-flex size-8 items-center justify-center rounded border border-gray-100 bg-white text-gray-900 rtl:rotate-180"
                            >
                                <span className="sr-only">Pagina urmatoare</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-3 w-3"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>}
                        </div>
                    </div>
                </div>

                {/* Mobile Version */}
                <div className="mx-auto max-w-screen-xl md:hidden z-0">
                    {!isLoadingSearch && data?.totalPages === 0 && <p>Nu au fost gasite rezultate.</p>}

                    <div className="flex justify-end mt-4 mr-2">
                        <button
                            className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700"
                            onClick={handleRemoveFilters}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                className="size-3"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                            </svg>

                            <p className="text-sm whitespace-nowrap ml-2">Elimina filtrele</p>
                        </button>
                    </div>

                    <div className="-my-3 py-3 ml-12 mt-2 mb-8 mr-12">
                        <SearchInput searchDeliveryNote={searchDeliveryNote} setSearchDeliveryNote={setSearchDeliveryNote} setIsNewSearch={setIsNewSearch}/>
                    </div>

                    <details className="group relative ml-12">
                        <summary
                            className="flex items-center gap-2 pb-1 text-gray-700 transition-colors [&::-webkit-details-marker]:hidden"
                        >
                            <span className="text-sm font-medium"> Cauta comenzile din </span>

                            <span className="transition-transform group-open:-rotate-180">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.5"
                                        stroke="currentColor"
                                        className="size-4"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round"
                                            d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                                    </svg>
                                  </span>
                        </summary>

                        <div
                            className="z-100 w-64 divide-y divide-gray-300 rounded border border-gray-300 bg-white shadow-sm group-open:absolute group-open:start-0 group-open:top-8"
                        >
                            <div className="flex items-center justify-between px-3 py-2">
                                <span className="text-sm text-gray-700"> Selecteaza datele </span>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setDateMin('');
                                        setDateMax(new Date())
                                    }}
                                    className="text-sm text-gray-700 underline transition-colors hover:text-gray-900"
                                >
                                    Reset
                                </button>
                            </div>

                            <div className="flex items-center gap-3 p-3">
                                <label htmlFor="MinPrice">
                                    <span className="text-sm text-gray-700"> Data de inceput </span>

                                    <DatePicker
                                        selected={dateMin}
                                        onChange={(dateMin) => setDateMin(dateMin)}
                                        dateFormat="dd/MM/yyyy"
                                        className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                        popperPlacement="bottom-start"
                                        popperModifiers={[
                                            {
                                                name: "preventOverflow",
                                                options: {boundary: "viewport", tether: false},
                                                fn: () => ({}),
                                            },
                                            {
                                                name: "flip",
                                                options: {fallbackPlacements: []}, // Prevents flipping above
                                                fn: () => ({}),
                                            },
                                        ]}
                                        portalId="root-portal" // Ensures calendar is rendered outside restrictive containers
                                    />
                                </label>

                                <label htmlFor="MaxPrice">
                                    <span className="text-sm text-gray-700"> Data de sfarsit </span>

                                    <DatePicker
                                        selected={dateMax}
                                        onChange={(dateMax) => setDateMax(dateMax)}
                                        dateFormat="dd/MM/yyyy"
                                        className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                        popperPlacement="bottom-start"
                                        popperModifiers={[
                                            {
                                                name: "preventOverflow",
                                                options: {boundary: "viewport", tether: false},
                                                fn: () => ({}),
                                            },
                                            {
                                                name: "flip",
                                                options: {fallbackPlacements: []},
                                                fn: () => ({}),
                                            },
                                        ]}
                                        portalId="root-portal"
                                    />
                                </label>
                            </div>
                        </div>
                    </details>

                    {data?.items.map((deliveryNote: DeliveryNoteModel) => {
                        return (
                            <div
                                className="flow-root rounded-lg border border-gray-100 py-3 ml-12 mt-8 mb-8 mr-12 shadow-sm">
                                <dl className="-my-3 divide-y divide-gray-100 text-sm">
                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Data generarii avizului</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{deliveryNote.deliveryNoteDate?.split("T")[0]}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Ultima actualizare</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{deliveryNote.deliveryNoteDate?.split("T")[0]}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Status Aviz</dt>
                                        {deliveryNote.documentStatusId === 360 &&
                                            <dd className="text-gray-700 sm:col-span-2">Generat</dd>}
                                        {deliveryNote.documentStatusId === 370 &&
                                            <dd className="text-gray-700 sm:col-span-2">In Progres</dd>}
                                        {deliveryNote.documentStatusId === 380 &&
                                            <dd className="text-gray-700 sm:col-span-2">Finalizat</dd>}
                                        {deliveryNote.documentStatusId === 390 &&
                                            <dd className="text-gray-700 sm:col-span-2">In Disputa</dd>}
                                        {deliveryNote.documentStatusId === 400 &&
                                            <dd className="text-gray-700 sm:col-span-2">Disputa Solutionata</dd>}
                                        {deliveryNote.documentStatusId === 410 &&
                                            <dd className="text-gray-700 sm:col-span-2">Anulat</dd>}
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Nume magazin</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{deliveryNote.warehouseName}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Nume francizat</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{deliveryNote.workingPoint.workingPointName}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Nr aviz de expeditie</dt>
                                        {deliveryNote.deliveryNoteNumber ? <dd
                                            className="text-gray-700 sm:col-span-2">{deliveryNote.deliveryNoteSeries} {deliveryNote.deliveryNoteNumber}
                                        </dd> : <dd
                                            className="text-gray-700 sm:col-span-2"> - </dd> }
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Nr precomanda</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{deliveryNote.order.preOrderId}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Nr comanda</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{deliveryNote.orderId}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Editeaza</dt>
                                        <dd className="text-gray-700 sm:col-span-2"><a
                                            href={"/my-account/delivery-note/" + deliveryNote.id}
                                            className="text-blue-500 underline z-auto">Editeaza avizul</a>
                                        </dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Descarca</dt>
                                        {[380, 390, 400, 410].includes(deliveryNote.documentStatusId) ?
                                            <dd className="text-gray-700 sm:col-span-2 flex flex-col">
                                                <button
                                                    onClick={() => handleDownloadDeliveryNotePDF(deliveryNote.deliveryNoteNumber)}
                                                    className="mb-1 text-left font-medium text-red-600 hover:text-red-500">
                                                    Descarca Aviz
                                                </button>
                                                <button
                                                    onClick={() => handleDownloadDeliveryNoteConformityPDF(deliveryNote.deliveryNoteNumber)}
                                                    className="mb-1 text-left font-medium text-red-600 hover:text-red-500">
                                                    Declaratie de conformitate
                                                </button>
                                            </dd> : <dd className="text-gray-700 sm:col-span-2 flex flex-col"> - </dd>}
                                    </div>
                                </dl>
                            </div>
                        );
                    })}

                    <div className="text-center mb-4">
                        <div className="inline-flex text-center items-center justify-center gap-3">
                            <div
                                onClick={(e) => {
                                    if (currentPage >= 1) handlePageChange(currentPage - 1);
                                    e.preventDefault()
                                }}
                                className="inline-flex size-8 items-center justify-center rounded border border-gray-100 bg-white text-gray-900 rtl:rotate-180"
                            >
                                <span className="sr-only">Pagina anterioara</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-3 w-3"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>

                            <p className="text-xs text-gray-900">
                                {currentPage + 1}<span className="mx-0.25">/</span>
                                {data !== undefined && Math.ceil(data.totalCount / pageSize)}
                            </p>

                            {data !== undefined && <div
                                onClick={(e) => {
                                    if (currentPage < data.totalPages - 1) handlePageChange(currentPage + 1);
                                    e.preventDefault()
                                }}
                                className="inline-flex size-8 items-center justify-center rounded border border-gray-100 bg-white text-gray-900 rtl:rotate-180"
                            >
                                <span className="sr-only">Pagina urmatoare</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-3 w-3"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
